/* Public Shopify listing fields only. No WMS catalogue or SKU-level fitment union. */
(function (root) {
  'use strict';
  const TTL = 15 * 60 * 1000;
  const text = value => String(value || '').normalize('NFKC').replace(/\s+/g, ' ').trim();
  const key = value => text(value).toLocaleLowerCase('de');
  const list = value => Array.isArray(value) ? value.filter(v => typeof v === 'string').map(text).filter(Boolean) : typeof value === 'string' && value.trim() ? [text(value)] : [];
  const brandAliases = ['Segway-Ninebot', 'Ninebot-Segway', 'Segway Ninebot', 'Ninebot Segway', 'Ninebot', 'Segway'];
  const brand = value => brandAliases.some(a => key(a) === key(value)) ? 'Segway-Ninebot' : text(value);
  const excluded = /^(generic|universal|sonstige|cst|tuovt|patona|hayes)$/i;
  const id = value => 'sf-' + encodeURIComponent(key(value));
  function modelName(value) {
    return text(value).replace(/^soflow\s+s0(?=\d)/i, 'SoFlow SO').replace(/\bgen\s*(\d+)\b/ig, 'Gen$1');
  }
  function build(products) {
    const brandNames = new Map(), brands = new Map();
    for (const product of products) for (const name of list(product.brands)) {
      if (excluded.test(name)) continue;
      if (!brands.has(name)) brands.set(name, brand(name));
      const canonical = brands.get(name);
      brandNames.set(key(canonical), canonical);
    }
    const prefixes = [...brandNames.values()].flatMap(name => (name === 'Segway-Ninebot' ? brandAliases : [...new Set([name, name.replace(/\s/g, ''), name.normalize('NFD').replace(/\p{M}/gu, '')])]).map(alias => ({ alias, name, normalized: key(alias) + ' ' })))
      .sort((a, b) => b.alias.length - a.alias.length);
    const prefixWords = new Map(), prefixCache = new Map();
    // Keep the existing longest-alias order within each normalized first word.
    for (const p of prefixes) {
      const first = p.normalized.split(' ', 1)[0];
      if (!prefixWords.has(first)) prefixWords.set(first, []);
      prefixWords.get(first).push(p);
    }
    const prefix = label => {
      if (!prefixCache.has(label)) {
        const normalized = key(label), candidates = prefixWords.get(normalized.split(' ', 1)[0]) || [];
        prefixCache.set(label, candidates.find(p => normalized.startsWith(p.normalized)));
      }
      return prefixCache.get(label);
    };
    const split = label => {
      const pieces = label.split(/\s*[,;]\s*/);
      // A decimal comma or an abbreviated model list is never expanded into fitment.
      return pieces.length > 1 && pieces.every(piece => prefix(piece)) ? pieces : [label];
    };
    const vehicles = new Map(), productModels = Object.create(null), records = Object.create(null);
    const skus = Object.create(null), variantSkus = Object.create(null), variantConditions = Object.create(null);
    for (const product of products) {
      if (!/^\d+$/.test(product.id) || records[product.id] || !Array.isArray(product.variants)) throw new Error('Invalid Shopify listing catalogue');
      records[product.id] = product;
      const ids = new Set();
      // Only explicitly branded model values establish the parent. Co-occurrence
      // of several brands and models on a listing is not a brand/model relation.
      for (const original of list(product.models).flatMap(split)) {
        const p = prefix(original);
        if (!p) continue;
        let remainder = original.slice(p.alias.length).trim();
        // Some Shopify values repeat the same manufacturer twice.
        let repeated;
        while ((repeated = prefix(remainder))?.name === p.name) remainder = remainder.slice(repeated.alias.length).trim();
        const label = modelName(p.name + ' ' + remainder);
        const suffix = label.slice(p.name.length).trim();
        if (!suffix || /^(e[ -]?scooter|scooter|alle|generic|universal)$/i.test(suffix) || /[,;]/.test(suffix.replace(/\d,\d/g, ''))) continue;
        const ident = id(label);
        if (!vehicles.has(ident)) vehicles.set(ident, { id: ident, brandId: id(p.name), brand: p.name, label, aliases: new Set() });
        const vehicle = vehicles.get(ident);
        const aliases = [original, label, label.replace(/Gen(\d+)/g, 'Gen $1')];
        if (p.name === 'Segway-Ninebot') for (const alias of brandAliases) aliases.push(alias + ' ' + suffix);
        for (const alias of aliases) vehicle.aliases.add(alias);
        ids.add(ident);
      }
      productModels[product.id] = [...ids];
      for (const variant of product.variants) {
        if (!/^\d+$/.test(variant.id) || Object.hasOwn(variantSkus, variant.id)) throw new Error('Duplicate Shopify variant');
        const sku = text(variant.sku).toUpperCase();
        variantSkus[variant.id] = sku;
        if (sku) skus[sku] = []; // Identity / exact-SKU routing only, never compatibility.
        if (product.condition) variantConditions[variant.id] = product.condition;
      }
    }
    for (const vehicle of vehicles.values()) vehicle.aliases = [...vehicle.aliases];
    return { version: 2, source: 'shopify', vehicles: [...vehicles.values()].sort((a, b) => a.label.localeCompare(b.label, 'de')), productModels, records, skus, variantSkus, variantConditions };
  }
  async function pages(request) {
    const first = await request(1);
    if (first.version !== 2 || first.source !== 'shopify' || !Number.isInteger(first.total) || first.total < 0 ||
        !Number.isInteger(first.pages) || first.pages !== Math.ceil(first.total / 250) || first.total > 25000) throw new Error('Invalid Shopify pagination');
    const results = new Array(Math.max(1, first.pages));
    function accept(page, number) {
      const count = Math.min(250, Math.max(0, first.total - (number - 1) * 250));
      if (page.version !== 2 || page.source !== 'shopify' || page.page !== number || page.total !== first.total ||
          page.pages !== first.pages || !Array.isArray(page.products) || page.products.length !== count) throw new Error('Incomplete Shopify catalogue');
      results[number - 1] = page.products;
    }
    accept(first, 1);
    let next = 2;
    await Promise.all(Array.from({ length: Math.min(6, Math.max(0, first.pages - 1)) }, async () => {
      while (next <= first.pages) { const number = next++; accept(await request(number), number); }
    }));
    const products = results.flat();
    if (new Set(products.map(p => p.id)).size !== first.total) throw new Error('Shopify catalogue changed during pagination');
    return products;
  }
  let pending, vehiclesPending;
  function loadVehicles() {
    if (vehiclesPending) return vehiclesPending;
    vehiclesPending = (async () => {
      const config = document.querySelector('[data-shopify-catalog]');
      if (!config?.dataset.esvVehicles) return null;
      let data;
      if (root.__esvCatalogPrefetch?.url === config.dataset.shopifyCatalog && root.__esvCatalogPrefetch.vehicles) {
        data = await root.__esvCatalogPrefetch.vehicles;
      } else {
        const response = await fetch(config.dataset.esvVehicles, { signal: AbortSignal.timeout(15000) });
        if (!response.ok) return null;
        data = await response.json();
      }
      if (data?.version !== 1 || !Array.isArray(data.vehicles) || !data.vehicles.every(vehicle => vehicle &&
          ['id', 'brandId', 'brand', 'label'].every(field => typeof vehicle[field] === 'string' && vehicle[field].trim()) &&
          Array.isArray(vehicle.aliases) && vehicle.aliases.every(alias => typeof alias === 'string'))) return null;
      return data.vehicles;
    })().catch(() => null);
    return vehiclesPending;
  }
  function invalidate() {
    const config = document.querySelector('[data-shopify-catalog]');
    try { if (config) sessionStorage.removeItem('esv:shopify-catalog:v2:' + config.dataset.shopifyCatalog); } catch (_) { /* Storage may be unavailable. */ }
    pending = null;
  }
  async function load() {
    if (pending) return pending;
    pending = (async () => {
      const config = document.querySelector('[data-shopify-catalog]');
      if (!config) throw new Error('Missing Shopify catalogue URL');
      const cacheKey = 'esv:shopify-catalog:v2:' + config.dataset.shopifyCatalog;
      try {
        const saved = JSON.parse(sessionStorage.getItem(cacheKey));
        if (saved && Date.now() - saved.at >= 0 && Date.now() - saved.at < TTL) return build(saved.products);
      } catch (_) { /* A full live read remains available. */ }
      const products = await pages(async number => {
        if (number === 1 && root.__esvCatalogPrefetch?.url === config.dataset.shopifyCatalog && root.__esvCatalogPrefetch.first) {
          const first = root.__esvCatalogPrefetch.first;
          delete root.__esvCatalogPrefetch.first;
          try { return await first; } catch (_) { /* Retry with a live read. */ }
        }
        const url = new URL(config.dataset.shopifyCatalog, location.origin);
        url.searchParams.set('page', number);
        const response = await fetch(url, { signal: AbortSignal.timeout(20000) });
        if (!response.ok) throw new Error('Shopify catalogue unavailable');
        return response.json();
      });
      const catalogue = build(products);
      try { sessionStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), products })); } catch (_) { /* Private browsing / quota: keep the in-page promise. */ }
      return catalogue;
    })().then(catalogue => {
      root.document?.dispatchEvent(new root.CustomEvent('esv:catalogue-ready', { detail: { vehicles: catalogue.vehicles } }));
      return catalogue;
    }).catch(error => { pending = null; throw error; });
    return pending;
  }
  function family(vehicles, selectedBrand, label) {
    const familyKey = key(label).replace(/^xiaomi\s+/, '');
    return vehicles.filter(v => key(v.brand) === key(brand(selectedBrand)) &&
      (' ' + key(v.label).replace(/[^\p{L}\p{N}]+/gu, ' ') + ' ').includes(' ' + familyKey + ' '));
  }
  function restrictQuery(query, catalogue, vehicle) {
    if (!vehicle) return query;
    const ids = Object.keys(catalogue.productModels).filter(id => catalogue.productModels[id].includes(vehicle));
    // Bound query length. Larger model assortments retain complete cursor traversal.
    if (ids.length > 100) return query;
    const filter = '(' + (ids.length ? ids.map(id => 'id:' + id).join(' OR ') : 'id:0') + ')';
    return query ? '(' + query + ') AND ' + filter : filter;
  }
  const api = { build, pages, load, loadVehicles, invalidate, family, id, restrictQuery };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.EsvShopifyCatalog = api;
})(typeof window === 'undefined' ? globalThis : window);
